import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { adminApi } from '@/api/admin'
import { ApiError } from '@/api/errors'
import type { MfaState, StaffMe } from '@/api/types'

/**
 * The signed-in staff member. `user` is set only after MFA has passed;
 * `pendingMfa` says what the session still needs after the password step.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<StaffMe | null>(null)
  const pendingMfa = ref<MfaState | null>(null)
  const loaded = ref(false)

  const isSignedIn = computed(() => user.value !== null)
  const permissions = computed(() => new Set(user.value?.permissions ?? []))
  const isOwner = computed(() => user.value?.roles.includes('super_admin') ?? false)

  /** UI hint only: the API checks every permission itself (ADR-003). */
  function can(permission: string): boolean {
    return isOwner.value || permissions.value.has(permission)
  }

  /** Loads the session once per page load. A 401 or MFA_REQUIRED just means "not signed in yet". */
  async function load(): Promise<void> {
    if (loaded.value) return
    try {
      user.value = await adminApi.me()
      pendingMfa.value = null
    } catch (error) {
      const apiError = ApiError.from(error)
      user.value = null
      if (apiError.code === 'MFA_REQUIRED') {
        pendingMfa.value =
          apiError.details[0]?.mfa === 'enrollment_required' ? 'enrollment_required' : 'required'
      } else if (apiError.status !== 401 && apiError.status !== 403) {
        throw apiError
      }
    } finally {
      loaded.value = true
    }
  }

  async function login(email: string, password: string): Promise<MfaState> {
    const mfa = await adminApi.login(email, password)
    user.value = null
    pendingMfa.value = mfa
    return mfa
  }

  function signedIn(me: StaffMe): void {
    user.value = me
    pendingMfa.value = null
    loaded.value = true
  }

  /** Clears local state (after sign-out, idle timeout or a 401). */
  function reset(): void {
    user.value = null
    pendingMfa.value = null
    loaded.value = true
  }

  async function logout(): Promise<void> {
    try {
      await adminApi.logout()
    } finally {
      reset()
    }
  }

  return { user, pendingMfa, loaded, isSignedIn, isOwner, can, load, login, signedIn, reset, logout }
})
